import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Avatar, Box, Chip, Skeleton, Stack, Typography } from "@mui/material";
import React from "react";

import { widgetSmallHeight } from "../../../utils";
import { WidgetComponentCharts } from "../../common/WidgetComponentCharts";
import { WidgetMenuActions } from "../../common/WidgetMenuActions/WidgetMenuActions";
import { SASMenuStyles } from "../../ui/styles/inputStyles";
import { useInstalledAppVersionsQuery } from "./hooks/useInstalledAppVersionsQuery";
import { InstalledAppVersions } from "./InstalledAppVersions";
import { AppVersionItem, InstalledAppVersionsData } from "./types";

interface AppVersionsWidgetProps {
  data?: InstalledAppVersionsData;
}

export const AppVersionsWidget: React.FC<AppVersionsWidgetProps> = ({ data }) => {
  const { data: fetchedData, isLoading, refetch } = useInstalledAppVersionsQuery();

  const currentData = data || fetchedData;
  const items = currentData?.items || [];
  const previewItems = items.slice(0, 3);
  const counts = currentData?.counts || { workforces: 0, users: 0, outdated: 0, unsupported: 0 };
  const total = currentData?.total || 0;

  const getWidgetActions = () => [
    {
      label: "See App Versions",
      icon: <VisibilityIcon />,
      modalComponent: <InstalledAppVersions initialData={currentData || undefined} onRefresh={refetch} />,
      modalKey: "Dashboard-App-Versions-List",
    },
  ];

  return (
    <WidgetComponentCharts
      swapyName="app-versions"
      sxProp={{ height: widgetSmallHeight }}
      title="Flow Mobile Versions"
      icon={<SmartphoneIcon />}
      description={
        isLoading && !currentData
          ? "Loading version statistics..."
          : `${counts.outdated} outdated • ${counts.unsupported} unsupported`
      }
      actions={<WidgetMenuActions actionList={getWidgetActions()} sx={SASMenuStyles} />}
      data={
        <Box sx={{ width: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {/* Quick Metrics Header Bar */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1,
              bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
              p: 1.25,
              borderRadius: "12px",
              border: "1px solid var(--fx-surface-border, #E2E8F0)",
            }}
          >
            <Box>
              <Typography fontSize={11} fontWeight={600} color="var(--fx-surface-textsecondary, #64748B)">
                Total
              </Typography>
              {isLoading && !currentData ? (
                <Skeleton variant="text" width={40} height={24} />
              ) : (
                <Typography fontSize={16} fontWeight={800} color="var(--fx-surface-text, #0F172A)">
                  {total}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography fontSize={11} fontWeight={600} color="#B45309">
                Outdated
              </Typography>
              {isLoading && !currentData ? (
                <Skeleton variant="text" width={40} height={24} />
              ) : (
                <Typography fontSize={16} fontWeight={800} color="#D97706">
                  {counts.outdated}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography fontSize={11} fontWeight={600} color="#9F1239">
                Unsupported
              </Typography>
              {isLoading && !currentData ? (
                <Skeleton variant="text" width={40} height={24} />
              ) : (
                <Typography fontSize={16} fontWeight={800} color="#E11D48">
                  {counts.unsupported}
                </Typography>
              )}
            </Box>
          </Box>

          {/* List Preview */}
          <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {isLoading && !currentData ? (
              Array.from({ length: 3 }).map((_, idx) => (
                <Box
                  key={idx}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={1}
                  sx={{ borderRadius: "10px", bgcolor: "var(--fx-surface-base, #FFFFFF)", border: "1px solid var(--fx-surface-border, #E2E8F0)" }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Skeleton variant="circular" width={28} height={28} />
                    <Box>
                      <Skeleton variant="text" width={100} height={18} />
                      <Skeleton variant="text" width={60} height={14} />
                    </Box>
                  </Box>
                  <Skeleton variant="rounded" width={70} height={20} />
                </Box>
              ))
            ) : previewItems.length === 0 ? (
              <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                <Typography variant="body2" color="var(--fx-surface-texttertiary, #94A3B8)" fontWeight={500}>
                  No mobile app versions installed
                </Typography>
              </Box>
            ) : (
              previewItems.map((item: AppVersionItem) => (
                <Box
                  key={item.uid}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    p: 1,
                    borderRadius: "10px",
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    border: "1px solid var(--fx-surface-border, #E2E8F0)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: item.targetType === "workforce" ? "var(--fx-colors-info-100, #DBEAFE)" : "var(--fx-colors-success-100, #D1FAE5)",
                        color: item.targetType === "workforce" ? "#1E40AF" : "#0F766E",
                      }}
                    >
                      {item.name ? item.name.charAt(0).toUpperCase() : "U"}
                    </Avatar>
                    <Box minWidth={0}>
                      <Typography variant="body2" fontSize={13} fontWeight={600} color="var(--fx-surface-text, #0F172A)" noWrap>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" fontSize={11} color="var(--fx-surface-textsecondary, #64748B)" noWrap display="block">
                        {item.companyName}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
                    <Chip
                      icon={
                        (item.appPlatform || "").toLowerCase() === "ios" ? (
                          <AppleIcon style={{ fontSize: 12 }} />
                        ) : (
                          <AndroidIcon style={{ fontSize: 12 }} />
                        )
                      }
                      label={`v${item.appVersion}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: 10,
                        fontWeight: 700,
                        borderRadius: "6px",
                        bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                      }}
                    />

                    {item.isSupported && item.isUpToDate ? (
                      <Chip
                        icon={<CheckCircleOutlineIcon style={{ fontSize: 12 }} />}
                        label="OK"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: "var(--fx-colors-success-100, #D1FAE5)",
                          color: "#15803D",
                          borderRadius: "6px",
                        }}
                      />
                    ) : !item.isSupported ? (
                      <Chip
                        icon={<ErrorOutlineIcon style={{ fontSize: 12 }} />}
                        label="Obsolete"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: "var(--fx-colors-danger-50, #FEF2F2)",
                          color: "#BE123C",
                          borderRadius: "6px",
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<WarningAmberIcon style={{ fontSize: 12 }} />}
                        label="Update"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: "var(--fx-colors-yellow-100, #FFF9C2)",
                          color: "#B45309",
                          borderRadius: "6px",
                        }}
                      />
                    )}
                  </Stack>
                </Box>
              ))
            )}
          </Stack>
        </Box>
      }
    />
  );
};

export default AppVersionsWidget;
